import { useEffect, useState } from "react";
import { Link, useNavigate } from "@tanstack/react-router";
import {
  Images,
  Sparkles,
  UserRound,
  AlertTriangle,
  Play,
  Gavel,
  Timer,
  Building2,
} from "lucide-react";
import { SpriteIcon } from "@/components/icons/IconSprite";
import { categoryIcon, categoryLabel } from "@/lib/categories";
import { timeLeftLabel, endsSoon } from "@/lib/auctions";
import { adImageSrc, adImageSrcSet } from "@/lib/image-src";
import { parseFocus } from "@/lib/image-focus";
import { FocusedImage } from "@/components/FocusedImage";
import { relativePublished, formatPublished } from "@/lib/format-date";
import { useHydrated } from "@/hooks/useHydrated";
import { useAuctionsEnabled } from "@/lib/auction-availability";
import { AuctionDevBadge } from "@/components/AuctionDevBadge";
import { AdminAdTooltip } from "@/components/AdminAdTooltip";
import { ShareMenu } from "@/components/ShareMenu";
import { FavoriteButton } from "@/components/FavoriteButton";
import { useLocale } from "@/lib/i18n/locale-context";
import { formatPrice } from "@/lib/i18n/format";
import type { SellCountry } from "@/lib/i18n/config";




export type AdCardData = {
  slug: string;
  image_url: string;
  image_urls?: string[] | null;
  image_focus?: unknown;
  title: string;
  description: string;
  price: number;
  category: string | null;
  location: string | null;
  created_at?: string | null;
  promoted_until?: string | null;
  price_flag?: string | null;
  price_flag_note?: string | null;
  video_url?: string | null;
  sale_kind?: string | null;
  auction_ends_at?: string | null;
  auction_status?: string | null;
  sold_at?: string | null;
  /** Sant när säljaren är ett företagskonto. */
  seller_business?: boolean | null;
  country?: string | null;
};

export function AdCard({
  ad,
  distanceLabel,
  isOwn,
  topBid,
  priority,
}: {
  ad: AdCardData;
  distanceLabel?: string | null;
  isOwn?: boolean;
  /** Första kortet i listan laddas direkt – det är sidans största bild. */
  priority?: boolean;
  /** Ledande bud för auktioner, hämtas samlat av listan. */
  topBid?: { amount: number; count: number } | null;
}) {
  const hydrated = useHydrated();
  const navigate = useNavigate();
  const [imgFailed, setImgFailed] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const auctionsOn = useAuctionsEnabled();
  const { lang } = useLocale();
  const tx = lang === "en"
    ? { left: "left", sold: "Sold", ended: "Ended", promoted: "Promoted", yours: "Your listing", video: "Video", auction: "Auction", noImage: "No image", business: "Business", leading: "Leading bid", noBids: "Starting price · no bids yet", final: "Final price", starting: "Starting price", bid: "bid", bids: "bids", check: "Check the price before buying.", checkAria: "Check price", published: "Published" }
    : lang === "es"
      ? { left: "restantes", sold: "Vendido", ended: "Finalizada", promoted: "Destacado", yours: "Tu anuncio", video: "Vídeo", auction: "Subasta", noImage: "Sin imagen", business: "Empresa", leading: "Oferta más alta", noBids: "Precio inicial · sin ofertas", final: "Precio final", starting: "Precio inicial", bid: "oferta", bids: "ofertas", check: "Comprueba el precio antes de comprar.", checkAria: "Comprobar precio", published: "Publicado" }
      : { left: "kvar", sold: "Såld", ended: "Avslutad", promoted: "Framlyft", yours: "Din annons", video: "Film", auction: "Auktion", noImage: "Bild saknas", business: "Företag", leading: "Ledande bud", noBids: "Utgångspris · inga bud än", final: "Slutpris", starting: "Utgångspris", bid: "bud", bids: "bud", check: "Kontrollera priset innan du köper.", checkAria: "Kontrollera priset", published: "Publicerad" };
  const isAuction = ad.sale_kind === "auction" && auctionsOn;
  // Buden hämtas i webbläsaren – vänta med dem tills sidan hydrerats.
  const bidInfo = hydrated ? topBid : null;

  const promoted = !!ad.promoted_until && new Date(ad.promoted_until).getTime() > Date.now();

  // Nedräkningen tickar i webbläsaren – servern behöver inte tillfrågas.
  useEffect(() => {
    if (!isAuction) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [isAuction]);

  const endsMs = ad.auction_ends_at ? new Date(ad.auction_ends_at).getTime() : 0;
  const live = isAuction && ad.auction_status === "live" && endsMs > now;
  const lastHour = live && endsMs - now < 60 * 60 * 1000;
  const auctionLabel = live
    ? `${timeLeftLabel(ad.auction_ends_at, now, lang)} ${tx.left}`
    : ad.sold_at || ad.auction_status === "sold"
      ? tx.sold
      : tx.ended;
  const auctionTone = !live
    ? "bg-foreground/70 text-background"
    : lastHour
      ? "animate-pulse bg-red-600 text-white"
      : endsSoon(ad.auction_ends_at, now)
        ? "bg-amber-500 text-white"
        : "bg-background/90 text-foreground";


  return (
    <AdminAdTooltip slug={ad.slug}>

    <Link
      to="/a/$slug"
      params={{ slug: ad.slug }}
      className={`group flex h-full flex-col overflow-hidden rounded-lg border bg-card shadow-sm transition-shadow hover:shadow-md ${
        promoted ? "border-primary ring-1 ring-primary/30" : "border-border"
      }`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden bg-muted">
        <div className="absolute inset-x-2 top-2 z-10 flex h-7 items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-1 overflow-hidden">
            {promoted && (
              <span className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground">
                <Sparkles className="h-3 w-3" /> {tx.promoted}
              </span>
            )}
            {isOwn && hydrated && (
              <span className="inline-flex h-7 items-center gap-1 rounded-md bg-foreground/85 px-2 text-xs font-semibold text-background">
                <UserRound className="h-3 w-3" /> {tx.yours}
              </span>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {!isOwn && <FavoriteButton slug={ad.slug} size="sm" className="h-7 w-7" />}

            {ad.video_url && (
              <span className="inline-flex h-7 items-center gap-1 rounded-md bg-background/85 px-2 text-xs font-medium text-foreground">
                <Play className="h-3 w-3 fill-current" /> {tx.video}
              </span>
            )}
            {(ad.image_urls?.length ?? 0) > 1 && (
              <span className="inline-flex h-7 items-center gap-1 rounded-md bg-background/85 px-2 text-xs font-medium text-foreground">
                <Images className="h-3 w-3" /> {ad.image_urls!.length}
              </span>
            )}
          </div>
        </div>

        {isAuction && (
          <div className="absolute inset-x-2 bottom-2 z-10 flex items-center justify-between gap-2">
            <span className="inline-flex h-7 items-center gap-1 rounded-md bg-primary px-2 text-xs font-semibold text-primary-foreground">
              <Gavel className="h-3 w-3" /> {tx.auction}
            </span>
            <AuctionDevBadge />
            <span
              className={`inline-flex h-7 items-center gap-1 rounded-md px-2 text-xs font-semibold tabular-nums shadow-sm ${auctionTone}`}
            >
              <Timer className="h-3 w-3" />{" "}
              <span suppressHydrationWarning>{auctionLabel}</span>
            </span>
          </div>
        )}

        {imgFailed || !ad.image_url ? (
          <div className="flex h-full w-full flex-col items-center justify-center gap-2 bg-muted text-muted-foreground">
            {(() => {
              const Icon = categoryIcon(ad.category ?? "ovrigt");
              return <Icon className="h-8 w-8 opacity-60" />;
            })()}
            <span className="text-[11px] font-medium uppercase tracking-wide opacity-70">
              {tx.noImage}
            </span>
          </div>
        ) : (
          <FocusedImage
            focus={parseFocus(ad.image_focus)[0] ?? null}
            fallbackFit="cover"
            src={adImageSrc(ad.image_url, 600)}
            srcSet={adImageSrcSet(ad.image_url, [400, 600, 800])}
            sizes="(min-width: 1024px) 300px, (min-width: 640px) 45vw, 92vw"
            alt={ad.title}
            decoding="async"
            loading={priority ? "eager" : "lazy"}
            fetchPriority={priority ? "high" : undefined}
            onError={() => setImgFailed(true)}
            className="h-full w-full transition-transform duration-300 group-hover:scale-[1.03]"
          />
        )}
      </div>
      <div className="flex flex-1 flex-col p-4">
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            navigate({ to: "/annonser", search: { kategori: ad.category ?? "ovrigt" } });
          }}
          className="inline-flex w-fit items-center gap-1.5 rounded-md text-xs font-medium uppercase tracking-wide text-primary transition-colors hover:text-primary/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50"
        >
          {(() => {
            const Icon = categoryIcon(ad.category ?? "ovrigt");
            return <Icon className="h-3.5 w-3.5" />;
          })()}
          {categoryLabel(ad.category ?? "ovrigt")}
        </button>
        {ad.seller_business && (
          <span className="mt-1 inline-flex w-fit items-center gap-1 rounded-full border border-primary/30 bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary">
            <Building2 className="h-3 w-3" /> {tx.business}
          </span>
        )}

        <h3 className="mt-1 line-clamp-2 min-h-[3rem] font-heading text-base font-semibold text-foreground">
          {ad.title}
        </h3>
        <p className="mt-1 line-clamp-2 min-h-[2.5rem] text-sm text-muted-foreground">
          {ad.description}
        </p>
        <div className="mt-auto flex items-center justify-between pt-3">

          <span className="inline-flex flex-col font-heading text-lg font-bold text-foreground">
            {isAuction && (
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                {bidInfo?.amount
                  ? tx.leading
                  : live
                    ? tx.noBids
                    : ad.sold_at || ad.auction_status === "sold"
                      ? tx.final
                      : tx.starting}
                {(bidInfo?.count ?? 0) > 0 && (
                  <span className="font-medium normal-case text-muted-foreground">
                     ({bidInfo!.count} {bidInfo!.count === 1 ? tx.bid : tx.bids})
                  </span>
                )}
              </span>
            )}
            <span className="inline-flex items-center gap-1.5">
            {formatPrice(isAuction && bidInfo?.amount ? bidInfo.amount : ad.price, (ad.country === "ES" ? "ES" : "SE") satisfies SellCountry)}
            {(ad.price_flag === "low" || ad.price_flag === "high") && (
              <span
                className="inline-flex"
                title={ad.price_flag_note || tx.check}
              >
                <AlertTriangle
                  className="h-4 w-4 shrink-0 text-amber-500"
                  aria-label={tx.checkAria}
                />
              </span>
            )}
            </span>
          </span>

          {ad.location && (
            <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
              <SpriteIcon name="map-pin" className="h-3 w-3" /> {ad.location}
            </span>
          )}
        </div>
        {(ad.created_at || distanceLabel) && (
          <div className="mt-2 flex items-center justify-between gap-2">
            {ad.created_at ? (
              <p
                className="inline-flex items-center gap-1 text-xs text-muted-foreground"
                title={`${tx.published} ${formatPublished(ad.created_at, lang)}`}
              >
                <SpriteIcon name="calendar-days" className="h-3 w-3" /> {tx.published}{" "}
                {hydrated ? relativePublished(ad.created_at, lang) : formatPublished(ad.created_at, lang)}
              </p>
            ) : (
              <span />
            )}
            <div className="inline-flex items-center gap-2">
              {distanceLabel && (
                <span className="shrink-0 text-xs font-medium text-muted-foreground">
                  {distanceLabel}
                </span>
              )}
              <span
                className="inline-flex items-center"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
              >
                <ShareMenu
                  path={`/a/${ad.slug}`}
                  title={ad.title}
                  price={ad.price}
                  iconOnly
                  className="h-6 w-6 border-transparent text-muted-foreground hover:text-foreground"
                />
              </span>
            </div>
          </div>
        )}
      </div>
    </Link>
    </AdminAdTooltip>
  );
}

